import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Chart } from 'chart.js';
import { EMPTY, Subject, catchError, takeUntil, tap } from 'rxjs';
import { DashboardService } from '../../services/dashboard.service';
import { BuyerStatsSummary } from '../model/buyer-stats-summary.model';
import { PaymentTypes } from '../model/payment-types.model';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrl: './payments.component.scss'
})
export class PaymentsComponent implements OnInit, OnDestroy {
  constructor(private dashboardService: DashboardService) { }
  private unsubscribe$ = new Subject<void>()
  noDataAvailable: boolean = false;
  payments: Array<PaymentTypes> = new Array<PaymentTypes>();
  colorMap = new Map<string, string>();
  paymentColors: {[key:string]: string} = {
    fourDayPaymentPct: "#FBBA00",
    nextDayPaymentPct: "#0F84F5",
    premPaymentPct: "#6C13B0",
    otherPaymentPct: "#0BB0B0",
  }
  paymentLabels: {[key:string]: string} = {
    fourDayPaymentPct: "4 Working Day Payment",
    nextDayPaymentPct: "Next Working Day Payment",
    premPaymentPct: "Immediate Payment",
    otherPaymentPct: "Other",
  }
  @ViewChild('canvasWrapper', { static: false }) canvasWrapper: ElementRef;

  chart: Chart<"pie", number[], string>;

  ngOnInit(): void {
    this.dashboardService.buyerStatsData.pipe(
      tap(buyerStats => {
        this.noDataAvailable = this.checkNoDataAvailable(buyerStats);
        this.populatePaymentsArray(buyerStats);
        this.dashboardService.removeDomElement('no-data');
        if (this.noDataAvailable) {
          const noDataAvailableContainer = document.createElement('div');
          noDataAvailableContainer.id = 'no-data';
          noDataAvailableContainer.style.cssText = 'width:100%';
          noDataAvailableContainer.innerHTML = `<div style='display: flex; font-size: 0.8rem; justify-content: center; padding-top: 4.7rem; color: #4E4E4E;'>No Data Available</div>`;
          this.canvasWrapper.nativeElement.appendChild(noDataAvailableContainer);
        } else {
          this.createChart();
          this.dashboardService.removeDomElement('no-data');
        }
      }),
      catchError(() => {
        this.noDataAvailable = true;
        return EMPTY;
      }),
      takeUntil(this.unsubscribe$)).subscribe();
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }


  checkNoDataAvailable(buyerStats: BuyerStatsSummary) {
    return (buyerStats.nextDayPaymentPct === 0 || buyerStats.nextDayPaymentPct === null) &&
     (buyerStats.fourDayPaymentPct === 0 || buyerStats.fourDayPaymentPct === null) &&
      (buyerStats.premPaymentPct === 0 || buyerStats.premPaymentPct === null) &&
       (buyerStats.otherPaymentPct === 0 || buyerStats.otherPaymentPct === null);
  }

  populatePaymentsArray(buyerStats: BuyerStatsSummary) {
    if(this.payments.length > 0) this.payments = new Array<PaymentTypes>();
    for (const [key, value] of Object.entries(buyerStats)) {
      if (this.isPaymentKey(key) && value > 0) {
        this.payments.push({ key, value })
      }
    }
  }

  private isPaymentKey(key: string): boolean {
    return key.toLowerCase().includes('payment');
  }

  createChart() {
    const ctx = document.getElementById('payments-pie-chart') as HTMLCanvasElement;
    if (this.chart) {
      this.chart.destroy();
    }
    const data = {
      labels: this.payments.map(p => this.paymentLabels[p.key]),
      datasets: [{
        data: this.payments.map(p => p.value),
        backgroundColor: this.payments.map(p=> this.paymentColors[p.key])
      }]
    };

    this.chart = new Chart(ctx, {
      type: "pie",
      data: data,
      options: {
        radius: "100%",
        maintainAspectRatio: false,
        animation: {
          animateRotate: true,
          animateScale: true,
        },
        layout: {
        },
        plugins: {
          title: {
            display: false,
            font: {
              size: 12,
              weight: 700,
            }
          },
          tooltip:{
            callbacks:{
              label: function(context) {
                const chart = context.chart;
                const data = chart.data;
                const dataset = data.datasets[0].data as number[];
                const total = dataset.reduce((a,b) => a + b, 0);
                const value = dataset[context.dataIndex];
                const perc = ((value/total) * 100).toFixed(0);
                const label = data.labels?.[context.dataIndex] || '';

                return `${perc}% ${label}`;
              }
            }
          },
          legend: {
            display: true,
            position: "bottom",
            align: "start",
            title: {
              font: {
                size: 48,
              }
            },
            labels: {
              usePointStyle:true,
              pointStyle: 'rect',
              generateLabels: (chart) => {
                const data = chart.data;
                const total = (chart.data.datasets[0].data as number[]).reduce((a,b) => a + b, 0);

                return data.labels.map((label,i) => {
                  const value = chart.data.datasets[0].data[i] as number;
                  const perc = ((value/total) * 100).toFixed(0);

                  return{
                    text: `${perc}% ${label}`,
                    fillStyle: chart.data.datasets[0].backgroundColor[i],
                    strokeStyle: chart.data.datasets[0].backgroundColor[i],
                    index:i,
                    pointStyle: 'rect',
                  };
                });
              }
            },
          },
        },
      },
    });
  }
}
